/* eslint-disable camelcase, jsx-a11y/no-noninteractive-element-interactions */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Select from 'react-select';
import { toggleCalendar, toggleOptionCard } from '../../../actions/ViewController';
import { isDatesRange, validateValue } from '../../common/Utils';
import NumberOfWorkersSpinner from './numberofworkersspinner';
import { TServiceKey } from '../../../types';
import { TRootState } from '../../../reducers';
import { RoleIcon } from './RoleIcon';

type TRoleMainCardProps = {
    className?: string;
    service_key: TServiceKey;
    date: string;
    dates: string[];
    isMultiple: boolean;
    number_of_freelancers: number;
    updateField: (fieldName: string, value: any) => any;
    mode: string;
    isForJobEdit?: boolean;
    handleSwipe?: (index: number) => void;
};

export default function RoleMainCard({
    className,
    service_key,
    date,
    dates,
    isMultiple,
    number_of_freelancers,
    updateField,
    mode,
    isForJobEdit,
    handleSwipe,
}: TRoleMainCardProps) {
    const dispatch = useDispatch();

    const {
        dbsCheck,
        minStart,
        servicesStructure,
        user,
    } = useSelector((state: TRootState) => ({
        dbsCheck: state.requestData.bookingRequest.dbs_check,
        minStart: state.requestData.minStart,
        servicesStructure: state.servicesStructure,
        user: state.user,
    }));

    const isMinStartToday = () => moment(minStart).isSame(new Date(), 'day');

    const todayOrTomorrowSelected = () => {
        if (!validateValue(date)) return false;

        let todayOrTomorrow;
        if (isMinStartToday()) {
            todayOrTomorrow = isMultiple
                ? moment(dates[0]).isSame(new Date(), 'day') && dates.length === 1
                : moment(date).isSame(new Date(), 'day');
        } else {
            todayOrTomorrow = isMultiple
                ? moment(dates[0]).subtract(1, 'days').isSame(new Date(), 'day') && dates.length === 1
                : moment(date).subtract(1, 'days').isSame(new Date(), 'day');
        }
        return todayOrTomorrow;
    };

    const otherDaySelected = () => {
        if (!validateValue(date)) return false;

        return !todayOrTomorrowSelected();
    };

    const handleRoleFieldClick = (service: string) => {
        if (isForJobEdit) {
            if (service === service_key) {
                dispatch(toggleOptionCard('role'));
                if (handleSwipe) handleSwipe(1);
            }
        } else {
            dispatch(updateField('service', service));
            dispatch(toggleOptionCard('role'));

            if (handleSwipe) handleSwipe(1);
        }
    };

    const handleTimeFieldClick = (e: any) => {
        if (isForJobEdit) return;

        const todayDate = moment(new Date());
        if (e.target.value === 'today') {
            dispatch(updateField('date', todayDate.format('YYYY-MM-DD')));
            dispatch(updateField('isMultiple', false));
        } else if (e.target.value === 'tomorrow') {
            dispatch(updateField('date', todayDate.add(1, 'days').format('YYYY-MM-DD')));
            dispatch(updateField('isMultiple', false));
        } else if (!otherDaySelected()) {
            if (isMinStartToday()) {
                dispatch(updateField('date', todayDate.add(1, 'days').format('YYYY-MM-DD')));
            } else {
                dispatch(updateField('date', todayDate.add(2, 'days').format('YYYY-MM-DD')));
            }
        }
        dispatch(toggleOptionCard('time'));
        dispatch(toggleCalendar(e.target.value));
        if (handleSwipe) handleSwipe(2);
    };

    const otherDate = () => {
        if (otherDaySelected()) {
            if (dates.length) {
                return moment(dates[0]).format('DD MMM');
            }
            return moment(date, 'YYYY-MM-DD').format('DD MMM');
        }
        return 'Choose a day';
    };

    const renderRoleIcons = () => {
        if (servicesStructure) {
            return Object.entries(servicesStructure)
                .filter(([serviceKey, serviceData]) =>
                    !!serviceKey && (user.isAgent || serviceData.client_enabled !== false))
                .map(([serviceKey, serviceData]) =>
                    <RoleIcon
                        key={serviceKey}
                        value={serviceKey}
                        handleChange={() => handleRoleFieldClick(serviceKey)}
                        roleItemClassName="role-item service-role-item"
                        name={serviceData.label}
                        iconName={serviceData.icon_name}
                        service_key={service_key}
                        mode={mode}
                        activeLabelOnClick={() => handleRoleFieldClick(serviceKey)}
                    />
                );
        }
        return null;
    };

    const renderSelectedDate = () => {
        const numberOfDaysSelected = dates.length ? dates.length : 1;

        if (isMultiple && numberOfDaysSelected !== 1) {
            const sortedDates = dates.sort();
            const isRange = isDatesRange(sortedDates);

            if (isRange) {
                const formattedDateRange = `${moment(sortedDates[0]).format(
                    'D',
                )} \u2013 ${moment(sortedDates[sortedDates.length - 1]).format(
                    'D',
                )}`;
                const dateRangeMonth = moment(sortedDates[sortedDates.length - 1]).format('MMM');
                return (
                    <span>
                        <span className="nowrap-white-space">{formattedDateRange}</span>
                        {' '}{dateRangeMonth}
                    </span>
                );
            }
            return `${numberOfDaysSelected} days selected`;
        }
        return otherDate();
    };

    const timeItemClassName = `role-item${isForJobEdit ? ' disabled' : ''}`;

    return (
        <div className={`${className || ''} card card-role card-role-main`}>
            <div className="card-content">
                <p className="card-title">Service</p>
                <form className="postjob-form-roles">
                    {renderRoleIcons()}
                </form>
                <form>
                    <div className={timeItemClassName + (todayOrTomorrowSelected() ? ' active' : '')}>
                        {isMinStartToday() ? (
                            <label className="item-circle-wrapper">
                                <input
                                    type="radio"
                                    name="date"
                                    value="today"
                                    onChange={handleTimeFieldClick}
                                />
                                <span>Today</span>
                            </label>
                        ) : (
                            <label className="item-circle-wrapper">
                                <input
                                    type="radio"
                                    name="date"
                                    value="tomorrow"
                                    onChange={handleTimeFieldClick}
                                />
                                <span>Tomorrow</span>
                            </label>
                        )}
                    </div>
                    <div className={timeItemClassName + (otherDaySelected() ? ' active' : '')}>
                        <label
                            className="item-circle-wrapper"
                            onClick={handleTimeFieldClick}
                        >
                            <input
                                type="radio"
                                name="date"
                                value="choose"
                                onChange={handleTimeFieldClick}
                            />
                            <span>
                                {renderSelectedDate()}
                            </span>
                        </label>
                    </div>
                </form>
                <form>
                    <NumberOfWorkersSpinner
                        number_of_freelancers={number_of_freelancers}
                        updateField={updateField}
                    />
                </form>
                <form>
                    <div>
                        <p className="question">Does your job require a DBS check?</p>
                        <Select
                            className="card-role-select-dbs"
                            value={dbsCheck}
                            onChange={option => dispatch(updateField('dbs_check', option.value))}
                            options={[
                                { value: 'NO', label: 'No' },
                                { value: 'BA', label: 'DBS Basic' },
                                { value: 'EN', label: 'DBS Enhanced' },
                            ]}
                            clearable={false}
                            disabled={isForJobEdit}
                            searchable={false}
                        />
                    </div>
                </form>
            </div>
        </div>
    );
}
